import React, { Fragment, useCallback, useContext, useMemo } from 'react'
import { Button, Col, Form, message, Modal, Radio, Row, Select } from 'antd'
import { Formik } from 'formik'
import { TextField } from '@shared/modules/form/fields/text'
import { SelectField } from '@shared/modules/form/fields/select'
import { useServiceList } from '../../services/hooks/use-list'
import { useDoctorList } from '../../doctors/hooks/use-list'
import { RadioField } from '@shared/modules/form/fields/radio'
import { RangeField } from '@shared/modules/form/fields/date-range'
import { AppointmentsTableContext } from '../context'
import { Dayjs } from 'dayjs'
import { Constants } from '@shared/constants'
import { AppointmentsService } from '../services'
import { useInsuranceList } from '../../insurances/hooks/use-list'
import { v1 as uuid } from 'uuid'
import { nextTableFilterQueryMaker } from '@shared/modules/next-table/utils/query-maker'
import { useGetRegions } from '../../regions/hooks'

export type AppointmentsFilterFormData = {
  customer: {
    name: string
    gender: string
    insuranceNumber: string
    franchise: string
    passport: { secret: string }
    phoneNumber: string
    birthDate: [Dayjs, Dayjs] | null
  }
  regionId: string
  serviceIds: string[]
  serviceDoctorIds: string[]
  doctorIds: string[]
  insuranceId: string
  type: string
  scheduledAt: [Dayjs, Dayjs] | null
  totalPrice: string[]
}

export const AppointmentsTableFilter = ({ visible, onCancel }) => {
  const {
    handleChangeFilters,
    state: { filters },
  } = useContext(AppointmentsTableContext)
  const services = useServiceList()
  const doctors = useDoctorList()
  const insurances = useInsuranceList()
  const regions = useGetRegions()

  const onInsuranceExcel = useCallback(async () => {
    message.loading('Loading...')
    const result = await AppointmentsService.getInsuranceExcel()
    message.destroy()
    if (result.status === 200) {
      const anchor = document.createElement('a')
      const url = window.URL.createObjectURL(result.data)
      document.body.appendChild(anchor)
      anchor.href = url
      anchor.download = uuid()
      anchor.click()
      window.URL.revokeObjectURL(url)
    } else {
      message.error(result.data)
    }
  }, [])

  const serviceOptions = useMemo(
    () =>
      services.data.map((service) => (
        <Select.Option key={service.id} value={service.id.toString()}>
          {service.name}
        </Select.Option>
      )),
    [services],
  )

  const doctorOptions = useMemo(
    () =>
      doctors.data.map((doctor) => (
        <Select.Option key={doctor.id} value={doctor.id.toString()}>
          {doctor.firstname} {doctor.lastname}
        </Select.Option>
      )),
    [doctors.data],
  )

  const insuranceOptions = useMemo(
    () =>
      insurances.data.map((insurance) => (
        <Select.Option key={insurance.id} value={insurance.id.toString()}>
          {insurance.name}
        </Select.Option>
      )),
    [insurances.data],
  )

  const gendersNode = useMemo(
    () => [
      <Radio key='male' value='male'>
        Kişi
      </Radio>,
      <Radio key='female' value='female'>
        Qadın
      </Radio>,
    ],
    [],
  )

  const handleSubmit = useCallback(
    (values: AppointmentsFilterFormData) => {
      const filters: { id: string; value: any }[] = []

      if (values.customer.name) {
        filters.push({ id: 'client_name', value: values.customer.name })
      }

      if (values.customer.gender) {
        filters.push({ id: 'gender', value: values.customer.gender })
      }

      if (values.customer.insuranceNumber) {
        filters.push({ id: 'insurance_number', value: values.customer.insuranceNumber })
      }

      if (values.customer.franchise) {
        filters.push({ id: 'franchise', value: values.customer.franchise })
      }

      if (values.customer.passport.secret) {
        filters.push({ id: 'pin_number', value: values.customer.passport.secret })
      }

      if (values.customer.phoneNumber) {
        filters.push({ id: 'number', value: values.customer.phoneNumber })
      }

      if (values.customer.birthDate?.length) {
        filters.push({ id: 'birth_date', value: values.customer.birthDate.map((moment) => moment.format(Constants.DATE_TIME)).join(',') })
      }

      if (values.serviceIds.length) {
        filters.push({ id: 'service_id', value: values.serviceIds })
      }

      if (values.doctorIds.length) {
        filters.push({ id: 'visit_doctor_id', value: values.doctorIds })
      }

      if (values.type) {
        filters.push({ id: 'visit_doctor_id', value: values.type })
      }

      if (values.regionId) {
        filters.push({ id: 'region_id', value: values.regionId })
      }

      if (values.serviceDoctorIds.length) {
        filters.push({ id: 'service_doctor_id', value: values.serviceDoctorIds })
      }

      if (values.insuranceId.length) {
        filters.push({ id: 'insurance_id', value: values.insuranceId })
      }

      if (values.scheduledAt?.length) {
        filters.push({ id: 'visit_date', value: values.scheduledAt.map((moment) => moment.format(Constants.DATE_TIME)).join(',') })
      }

      if (values.totalPrice.length) {
        filters.push({ id: 'visit_total_price', value: values.totalPrice.join(',') })
      }

      handleChangeFilters(filters)
      onCancel()
    },
    [handleChangeFilters, onCancel],
  )

  const initialValues = useMemo(
    () => ({
      customer: {
        name: '',
        gender: '',
        passport: { secret: '' },
        phoneNumber: '',
        birthDate: null,

        insuranceNumber: '',
        franchise: '',
      },
      regionId: '',
      serviceIds: [],
      serviceDoctorIds: [],
      doctorIds: [],
      type: '',
      scheduledAt: null,
      totalPrice: [],
      insuranceId: '',
    }),
    [],
  )

  const exportExcel = useCallback(async () => {
    message.loading('Əməliyyat aparılır...', 0)
    const result = await AppointmentsService.exportExcel(nextTableFilterQueryMaker(filters))
    message.destroy()
    if (result.status === 200) {
      message.success('Sənəd hazırlanır...')
      const objectURL = URL.createObjectURL(result.data)
      const a = document.createElement('a')
      a.href = objectURL
      a.download = `appointment_${new Date().toISOString()}.xlsx`
      a.click()
    } else {
      message.error(result.data)
    }
  }, [filters])

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
      {({ handleSubmit, isSubmitting, values }) => (
        <Modal
          width={768}
          onCancel={onCancel}
          onOk={() => handleSubmit()}
          footer={
            <Fragment>
              <Button onClick={exportExcel}>Excel Export</Button>
              <Button onClick={onInsuranceExcel}>Sığorta Excel</Button>
              <Button onClick={onCancel}>Ləğv et</Button>
              <Button onClick={() => handleSubmit()}>Bəli</Button>
            </Fragment>
          }
          title='Filterlə'
          open={visible}
        >
          <Form style={{ marginBottom: -12 }} layout='vertical'>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <TextField name='customer.name' item={{ label: 'Müştərinin adı' }} />
              </Col>
              <Col xs={24} md={12}>
                <RadioField name='customer.gender' item={{ label: 'Cins' }}>
                  {gendersNode}
                </RadioField>
              </Col>
              <Col xs={24} sm={12}>
                <TextField name='customer.insuranceNumber' item={{ label: 'Sığorta nömrəsi' }} />
              </Col>
              <Col xs={24} sm={12}>
                <TextField name='customer.franchise' item={{ label: 'Franşiza' }} />
              </Col>
              <Col xs={24} sm={12}>
                <TextField name='customer.passport.secret' item={{ label: 'FİN kod' }} />
              </Col>
              <Col xs={24} sm={12}>
                <TextField name='customer.phoneNumber' item={{ label: 'Mobil nömrə' }} />
              </Col>
              <Col xs={24}>
                <RangeField name='customer.birthDate' item={{ label: 'Doğum tarixi' }} />
              </Col>
              <Col xs={24} sm={12}>
                <SelectField name='serviceIds' item={{ label: 'Xidmətlər' }} input={{ mode: 'multiple' }}>
                  {serviceOptions}
                </SelectField>
              </Col>
              <Col xs={24} sm={12}>
                <SelectField name='serviceDoctorIds' item={{ label: 'Xidmət göstərən həkimlər' }} input={{ mode: 'multiple' }}>
                  {doctorOptions}
                </SelectField>
              </Col>
              <Col xs={24} sm={12}>
                <SelectField name='doctorIds' item={{ label: 'Göndərən həkimlər' }} input={{ mode: 'multiple' }}>
                  {doctorOptions}
                </SelectField>
              </Col>
              <Col xs={24} sm={12}>
                <SelectField name='insuranceId' item={{ label: 'Sığorta şirkəti' }}>
                  {insuranceOptions}
                </SelectField>
              </Col>
              <Col xs={24} sm={12}>
                <SelectField name='type' item={{ label: 'Qəbul tipi' }}>
                  <Select.Option value='1'>Ambulator</Select.Option>
                  <Select.Option value='2'>Göndəriş</Select.Option>
                </SelectField>
              </Col>
              <Col xs={24} sm={12}>
                <SelectField name='regionId' item={{ label: 'Yaşadığı ərazi' }} input={{ placeholder: 'Yaşadığı ərazini seçin...' }}>
                  {regions.data?.map((elem) => (
                    <Select.Option value={elem.id.toString()} key={elem.id}>
                      {elem.name}
                    </Select.Option>
                  ))}
                </SelectField>
              </Col>
              <Col xs={24}>
                <RangeField name='scheduledAt' item={{ label: 'Qəbul tarixi' }} />
              </Col>
              <Col xs={12}>
                <TextField name='totalPrice.0' item={{ label: 'Ödənilmiş məbləğ aralığı' }} />
              </Col>
              <Col xs={12}>
                <TextField name='totalPrice.1' item={{ label: ' ' }} />
              </Col>
            </Row>
          </Form>
        </Modal>
      )}
    </Formik>
  )
}
